/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye } from "lucide-react";
import { useParams, useSearchParams } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import { useEffect, useState, useRef, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { Separator } from "@/app/_module/components/ui/separator";
import { toast } from "@/app/_module/components/ui/use-toast";
import { extractPhoneNumber } from "@/app/_module/utils/formatters";
import { getStateCode } from "@/app/_module/utils/state-mapping";
import { SmsChatWidget } from "@/app/_module/components/SmsChatWidget";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { useVoiceDeviceContext } from "@/app/_module/contexts/VoiceDeviceProvider";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { callService } from "@/app/_module/services/callService";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

import { LeadActionButtons } from "./LeadActionButtons";
import { LeadDetailsHeader } from "./LeadDetailsHeader";
import { LeadDetailsLayout } from "./LeadDetailsLayout";
import { LEAD_FORM_DEFAULTS } from "../constants/form-defaults";
import { useLead, useUpdateLead, useLeadWithPolicies, useCreateBeneficiaries, useUpdateBeneficiary } from "../hooks/queries";
import {
  updateLeadSchema,
  type UpdateLeadSchema,
} from "../schema/lead-schema";

/**
 * ✅ FIXED: Helper function to safely unwrap API responses
 * Handles multiple possible response structures from handleApiCall
 */
function unwrapApiResponse(response: any): any {
  if (!response) return null;

  // Try different possible structures
  // 1. Nested data.data (most common)
  if (response?.data?.data) return response.data.data;

  // 2. Direct data property
  if (response?.data) return response.data;

  // 3. Response is the object itself
  return response;
}

/**
 * Helper function for stable JSON stringification
 * Sorts object keys to ensure consistent comparison
 */
function stableStringify(obj: any): string {
  if (obj === null || obj === undefined) return String(obj);
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) {
    return '[' + obj.map(stableStringify).join(',') + ']';
  }
  const sortedKeys = Object.keys(obj).sort();
  const pairs = sortedKeys.map(key => `${JSON.stringify(key)}:${stableStringify(obj[key])}`);
  return '{' + pairs.join(',') + '}';
}

export interface LeadFullDetailsProps {
  /** When set, renders this lead instead of the route param (embedded mode). */
  leadId?: string;
  /** Embedded mode: back action instead of router-push to /leads. */
  onBack?: () => void;
  /** Embedded mode: prev/next lead swaps in place instead of route change. */
  onNavigateLead?: (leadId: string) => void;
}

const LeadFullDetails = function ({ leadId: leadIdProp, onBack, onNavigateLead }: LeadFullDetailsProps = {}) {
  const routeParams = useParams();
  const navigate = useRouter();
  const searchParams = useSearchParams();

  // Embedded-aware navigation: when hosted inside another view (e.g. the
  // Agent Details "Assigned Leads" tab) back/prev/next stay in that view.
  const goBack = useCallback(() => {
    if (onBack) onBack();
    else navigate.push(buildBackToLeadsUrlRef.current());
  }, [onBack, navigate]);
  const goToLead = useCallback((lid: string) => {
    if (onNavigateLead) onNavigateLead(lid);
    else navigate.push(`/leads/${lid}`);
  }, [onNavigateLead, navigate]);

  // Build URL to go back to leads page, preserving page/status/showAll/query params
  const buildBackToLeadsUrl = useCallback(() => {
    const params = new URLSearchParams();
    const page = searchParams.get("page");
    const status = searchParams.get("status");
    const showAll = searchParams.get("showAll");
    const query = searchParams.get("query");
    const sortBy = searchParams.get("sortBy");

    if (showAll === "true") {
      params.set("showAll", "true");
    } else if (status) {
      params.set("status", status);
    }
    if (page && page !== "1") {
      params.set("page", page);
    }
    if (query) {
      params.set("query", query);
    }
    if (sortBy) {
      params.set("sortBy", sortBy);
    }

    return params.toString() ? `/leads?${params.toString()}` : "/leads";
  }, [searchParams]);
  const buildBackToLeadsUrlRef = useRef(buildBackToLeadsUrl);
  buildBackToLeadsUrlRef.current = buildBackToLeadsUrl;

  const { mutateAsync: updateLeadMutation, isPending: isUpdating } =
    useUpdateLead();

  // Add beneficiary mutation hooks
  const createBeneficiariesMutation = useCreateBeneficiaries();
  const updateBeneficiaryMutation = useUpdateBeneficiary();
  const [isSavingBeneficiaries, setIsSavingBeneficiaries] = useState(false);

  const leadIdRaw = leadIdProp ?? routeParams?.id;
  const leadId = Array.isArray(leadIdRaw) ? leadIdRaw[0] : leadIdRaw;

  const { data: leadResponse, isLoading, error, refetch } = useLead(leadId || "");
  const { data: leadWithPoliciesResponse, isLoading: policiesLoading, refetch: refetchPolicies } = useLeadWithPolicies(leadId || "");

  // ✅ FIXED: Properly unwrap API responses
  const lead = unwrapApiResponse(leadResponse);
  const leadWithPolicies = unwrapApiResponse(leadWithPoliciesResponse) || {};

  // View-only: this lead was shared with the user (e.g. they were @mentioned)
  // but it belongs to another agent. They can see it but not edit/call/text it —
  // it stays on the owning agent's pipeline.
  const viewOnly =
    (lead as any)?.viewOnly === true || (leadWithPolicies as any)?.viewOnly === true;

  // Who holds it now. Present when the lead was reassigned away from this user
  // (the common case); absent for a pure @mention share.
  const viewOnlyOwner: string = String(
    (lead as any)?.assigned || (leadWithPolicies as any)?.assigned || '',
  ).trim();

  // Auto-redirect on 404 (lead not found / deleted / inaccessible).
  //
  // When this page is opened from a missed-call notification we attach
  // `?fallbackPhone=` in the click handler. If the lead 404s here (e.g. the
  // recipient of the missed call isn't the lead's current assignee, or the
  // lead is in a custom pipeline stage the agent doesn't have direct
  // detail-view access to), bounce them to a phone-filtered leads list with
  // showAll=true so they can see and click into the lead from there instead
  // of getting a dead-end "Lead not found" toast.
  useEffect(() => {
    if (!error) return;
    const msg = (error as any)?.message || (error as any)?.response?.status?.toString() || '';
    if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
      const fallbackPhone =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('fallbackPhone')
          : null;
      if (fallbackPhone && !onBack) {
        navigate.push(
          `/leads?query=${encodeURIComponent(fallbackPhone)}&showAll=true`,
        );
        return;
      }
      toast({ title: 'Lead not found', description: 'This lead may have been deleted.', variant: 'destructive' });
      goBack();
    }
  }, [error, navigate, goBack, onBack]);

  // Calculate hasPolicies and policyCount from the policies array
  const policies = Array.isArray(leadWithPolicies?.policies) ? leadWithPolicies.policies : [];
  const hasPolicies = policies.length > 0;
  const policyCount = policies.length;

  // Editing state - start in read-only mode
  const [isEditing, setIsEditing] = useState(false);
  const editingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const EDITING_TIMEOUT_MS = 60000; // 60 seconds of inactivity before locking

  // Lead navigation state - read from sessionStorage or fetch if not available
  const [leadNavigationList, setLeadNavigationList] = useState<string[]>([]);
  const [navigationContext, setNavigationContext] = useState<{
    currentPage: number;
    totalPages: number;
    totalLeads: number; // Total leads across all pages
    limit: number;
    queryParams: {
      searchTerm?: string;
      selectedStatus?: string;
      showAll?: boolean;
    };
  } | null>(null);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(true);

  // Track if navigation fetch has been attempted to prevent duplicate requests
  const navigationFetchAttemptedRef = useRef(false);

  // Prevent rapid spam clicking of navigation buttons
  // Use sessionStorage for cooldown to persist across soft navigations
  const NAVIGATION_COOLDOWN_MS = 800; // Minimum time between navigation clicks

  const canNavigate = useCallback(() => {
    const now = Date.now();
    const lastNavTime = parseInt(sessionStorage.getItem('lastLeadNavTime') || '0', 10);
    if (now - lastNavTime < NAVIGATION_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem('lastLeadNavTime', now.toString());
    return true;
  }, []);

  useEffect(() => {
    // Prevent duplicate fetches and memory leaks
    if (navigationFetchAttemptedRef.current) return;

    let isMounted = true;

    const loadNavigationContext = async () => {
      try {
        // Try to load full navigation context first from sessionStorage
        const storedContext = sessionStorage.getItem('leadNavigationContext');

        if (storedContext) {
          const parsed = JSON.parse(storedContext);

          if (parsed.leadIds && Array.isArray(parsed.leadIds)) {
            // Ensure lead IDs are strings (they might be objects or numbers)
            const normalizedIds = parsed.leadIds.map((id: any) =>
              typeof id === 'object' && id !== null ? (id.id || String(id)) : String(id)
            );

            if (isMounted) {
              setLeadNavigationList(normalizedIds);
              setNavigationContext({
                currentPage: parsed.currentPage || 1,
                totalPages: parsed.totalPages || 1,
                totalLeads: parsed.totalLeads || 0,
                limit: parsed.limit || 50,
                queryParams: parsed.queryParams || {},
              });
              setIsLoadingNavigation(false);
            }
            return;
          }
        }

        // Fallback to legacy key
        const storedList = sessionStorage.getItem('leadNavigationList');
        if (storedList) {
          const parsed = JSON.parse(storedList);
          if (Array.isArray(parsed)) {
            const normalizedIds = parsed.map((id: any) =>
              typeof id === 'object' && id !== null ? (id.id || String(id)) : String(id)
            );
            if (isMounted) {
              setLeadNavigationList(normalizedIds);
              setIsLoadingNavigation(false);
            }
            return;
          }
        }

        // No stored context - fetch leads for navigation (only once)
        navigationFetchAttemptedRef.current = true;

        try {
          const response: any = await api.get({
            url: getApiUrl('/leads?page=1&limit=50&status=New lead'),
            auth: true,
          });

          // Check if component is still mounted before updating state
          if (!isMounted) return;

          // Extract lead IDs from response (handle various response shapes)
          let leads: any[] = [];
          if (response?.data?.items) leads = response.data.items;
          else if (response?.data?.data?.items) leads = response.data.data.items;
          else if (Array.isArray(response?.data)) leads = response.data;
          else if (response?.items) leads = response.items;

          if (leads.length > 0) {
            const leadIds = leads.map((l: any) => String(l.id));
            const totalPages = response?.data?.meta?.totalPages || response?.meta?.totalPages || 1;
            const totalLeads = response?.data?.meta?.totalItems || response?.meta?.totalItems || leads.length;

            setLeadNavigationList(leadIds);
            setNavigationContext({
              currentPage: 1,
              totalPages,
              totalLeads,
              limit: 50,
              queryParams: { selectedStatus: 'New lead', showAll: false },
            });

            // Store in sessionStorage for future use (prevents re-fetch on navigation)
            const navContext = {
              leadIds,
              currentPage: 1,
              totalPages,
              totalLeads,
              limit: 50,
              queryParams: { selectedStatus: 'New lead', showAll: false },
            };
            sessionStorage.setItem('leadNavigationContext', JSON.stringify(navContext));
            sessionStorage.setItem('leadNavigationList', JSON.stringify(leadIds));
          }
        } catch (e) {
          // Silently fail - navigation just won't be available
        }
      } catch (e) {
        // Parsing error - silently fail
      } finally {
        if (isMounted) {
          setIsLoadingNavigation(false);
        }
      }
    };

    loadNavigationContext();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Calculate navigation state
  const currentLeadIndex = leadId ? leadNavigationList.indexOf(leadId) : -1;
  const hasLeadNavigation = leadNavigationList.length > 0 && currentLeadIndex !== -1;

  // Check if we can navigate prev/next within current page OR if there are more pages
  const canGoPrevInPage = hasLeadNavigation && currentLeadIndex > 0;
  const canGoNextInPage = hasLeadNavigation && currentLeadIndex < leadNavigationList.length - 1;
  const hasPrevPage = navigationContext && navigationContext.currentPage > 1;
  const hasNextPage = navigationContext && navigationContext.currentPage < navigationContext.totalPages;

  const hasPrevLead = canGoPrevInPage || hasPrevPage;
  const hasNextLead = canGoNextInPage || hasNextPage;

  // Fetch a different page of leads
  const fetchLeadsPage = useCallback(async (pageNum: number): Promise<string[] | null> => {
    if (!navigationContext) return null;

    try {
      setIsLoadingPage(true);
      const { queryParams, limit } = navigationContext;

      // Build query params
      const params = new URLSearchParams();
      params.set('page', pageNum.toString());
      params.set('limit', limit.toString());
      if (queryParams.searchTerm) params.set('query', queryParams.searchTerm);
      if (!queryParams.showAll && queryParams.selectedStatus) {
        params.set('status', queryParams.selectedStatus);
      }

      const response: any = await api.get({
        url: getApiUrl(`/leads?${params.toString()}`),
        auth: true,
      });

      // Extract lead IDs from response (handle various response shapes)
      let leads: any[] = [];
      if (response?.data?.items) leads = response.data.items;
      else if (response?.data?.data?.items) leads = response.data.data.items;
      else if (Array.isArray(response?.data)) leads = response.data;
      else if (response?.items) leads = response.items;

      return leads.map((l: any) => l.id);
    } catch (e) {
      console.error('Failed to fetch leads page:', e);
      return null;
    } finally {
      setIsLoadingPage(false);
    }
  }, [navigationContext]);

  const handlePrevLead = useCallback(async () => {
    // Prevent rapid spam clicking - sessionStorage persists across soft navigations
    if (!canNavigate()) return;

    if (canGoPrevInPage) {
      // Navigate within current page
      const prevLeadId = leadNavigationList[currentLeadIndex - 1];
      goToLead(prevLeadId);
    } else if (hasPrevPage && navigationContext) {
      // Fetch previous page and navigate to its last lead
      const prevPageLeads = await fetchLeadsPage(navigationContext.currentPage - 1);
      if (prevPageLeads && prevPageLeads.length > 0) {
        // Update navigation context
        const newContext = {
          leadIds: prevPageLeads,
          currentPage: navigationContext.currentPage - 1,
          totalPages: navigationContext.totalPages,
          totalLeads: navigationContext.totalLeads,
          limit: navigationContext.limit,
          queryParams: navigationContext.queryParams,
        };
        sessionStorage.setItem('leadNavigationContext', JSON.stringify(newContext));
        sessionStorage.setItem('leadNavigationList', JSON.stringify(prevPageLeads));
        setLeadNavigationList(prevPageLeads);
        setNavigationContext({
          ...navigationContext,
          currentPage: navigationContext.currentPage - 1,
        });
        // Navigate to last lead of previous page
        const lastLeadId = prevPageLeads[prevPageLeads.length - 1];
        goToLead(lastLeadId);
      }
    }
  }, [canNavigate, canGoPrevInPage, hasPrevPage, leadNavigationList, currentLeadIndex, goToLead, navigationContext, fetchLeadsPage]);

  const handleNextLead = useCallback(async () => {
    // Prevent rapid spam clicking - sessionStorage persists across soft navigations
    if (!canNavigate()) return;

    if (canGoNextInPage) {
      // Navigate within current page
      const nextLeadId = leadNavigationList[currentLeadIndex + 1];
      goToLead(nextLeadId);
    } else if (hasNextPage && navigationContext) {
      // Fetch next page and navigate to its first lead
      const nextPageLeads = await fetchLeadsPage(navigationContext.currentPage + 1);
      if (nextPageLeads && nextPageLeads.length > 0) {
        // Update navigation context
        const newContext = {
          leadIds: nextPageLeads,
          currentPage: navigationContext.currentPage + 1,
          totalPages: navigationContext.totalPages,
          totalLeads: navigationContext.totalLeads,
          limit: navigationContext.limit,
          queryParams: navigationContext.queryParams,
        };
        sessionStorage.setItem('leadNavigationContext', JSON.stringify(newContext));
        sessionStorage.setItem('leadNavigationList', JSON.stringify(nextPageLeads));
        setLeadNavigationList(nextPageLeads);
        setNavigationContext({
          ...navigationContext,
          currentPage: navigationContext.currentPage + 1,
        });
        // Navigate to first lead of next page
        const firstLeadId = nextPageLeads[0];
        goToLead(firstLeadId);
      }
    }
  }, [canNavigate, canGoNextInPage, hasNextPage, leadNavigationList, currentLeadIndex, goToLead, navigationContext, fetchLeadsPage]);

  // Auto-lock editing after inactivity
  const resetEditingTimeout = useCallback(() => {
    if (editingTimeoutRef.current) {
      clearTimeout(editingTimeoutRef.current);
    }
    if (isEditing) {
      editingTimeoutRef.current = setTimeout(() => {
        setIsEditing(false);
      }, EDITING_TIMEOUT_MS);
    }
  }, [isEditing]);

  // Reset timeout on user activity when editing
  useEffect(() => {
    if (!isEditing) {
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
      }
      return;
    }

    // Start the timeout when editing begins
    resetEditingTimeout();

    // Listen for user activity to reset timeout
    const handleActivity = () => {
      if (isEditing) {
        resetEditingTimeout();
      }
    };

    // Track mouse movement, key presses, and clicks
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
      }
    };
  }, [isEditing, resetEditingTimeout]);

  // SMS widget state
  const [showSmsChat, setShowSmsChat] = useState(false);
  const [smsMessages, setSmsMessages] = useState<Array<{
    id: string;
    text: string;
    direction: "inbound" | "outbound";
    timestamp: Date;
    // Full Twilio lifecycle so the bubble can recolor when the status webhook
    // updates the row (sending → sent → delivered, or → undelivered/failed).
    status?: "sending" | "queued" | "sent" | "delivered" | "failed" | "undelivered";
    messageSid?: string;
    errorCode?: string | null;
    errorMessage?: string | null;
  }>>([]);

  // Twilio device and user context for callbacks
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { user } = useUserContext();
  const {
    isDeviceReady,
    isDeviceRegistering,
    connect,
    forwardCallsToPhone,
  } = useTwilioDevice();

  // Get active call state and manual call lead status tracker
  // Need both activeCall (browser calls) AND bridgedCalls (phone forwarding) for hasActiveCall check
  const { activeCall, activeCallSid, bridgedCalls } = useVoiceDeviceContext();
  const { trackManualCallLeadStatus } = useUnifiedDialer();

  // Check for any active call (browser OR bridged/phone forwarding)
  const hasActiveCall = !!(activeCall && activeCallSid) || bridgedCalls.size > 0;

  // Subscribe to outbound SMS lifecycle updates so the chat bubble flips from
  // 'sent' to 'delivered'/'undelivered' in real time when Twilio's status
  // webhook fires. Mirrors the listener in SmsConversationContext but scoped
  // to this lead-detail page's local smsMessages state.
  const userId = user?.user?.id;
  useEffect(() => {
    if (!userId) return;
    let channel: any;
    let cancelled = false;
    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        const supabase = await getSupabaseClient();
        if (cancelled) return;
        channel = supabase
          .channel(`sms_status:${userId}`)
          .on('broadcast', { event: 'status_update' }, (payload: any) => {
            const { messageSid, messageStatus, errorCode, errorMessage } = payload.payload || payload;
            if (!messageSid || !messageStatus) return;
            setSmsMessages((prev) =>
              prev.map((m) =>
                m.messageSid === messageSid
                  ? { ...m, status: messageStatus as typeof m.status, errorCode: errorCode ?? null, errorMessage: errorMessage ?? null }
                  : m
              )
            );
          })
          .subscribe();
      } catch {}
    })();
    return () => {
      cancelled = true;
      try { channel?.unsubscribe?.(); } catch {}
    };
  }, [userId]);

  // Get user's default outbound phone from profile
  const userDefaultPhone = (user?.user as unknown as Record<string, unknown>)?.defaultOutboundPhone as string;

  // Select best caller ID using area code matching
  const selectBestCallerId = useCallback((targetPhone: string): string | undefined => {
    if (userDefaultPhone) return userDefaultPhone;

    const targetAreaCode = targetPhone.replace(/\D/g, '').slice(0, 3);
    const matchingPhone = phoneNumbers.find((phone: any) => {
      const phoneAreaCode = phone.phoneNumber?.replace(/\D/g, '').slice(0, 3);
      return phoneAreaCode === targetAreaCode;
    });

    if (matchingPhone) return matchingPhone.phoneNumber;
    if (phoneNumbers.length > 0) return phoneNumbers[0].phoneNumber;
    return undefined;
  }, [userDefaultPhone, phoneNumbers]);

  // Handle callback for inbound calls from history
  const handleCallBack = useCallback(async (phoneNumber: string) => {
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "Cannot call back - no phone number available.",
        variant: "destructive",
      });
      return;
    }

    if (phoneNumbers.length === 0) {
      toast({
        title: "No Active Phone Numbers",
        description: "You need at least one active phone number to make calls. Go to Settings > Phone Numbers to purchase one.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check user preference: Phone forwarding or browser calling
      if (forwardCallsToPhone) {
        // Use phone-to-phone method (call forwarding)
        await api.post({
          url: getApiUrl('/communication/sessions/manual-call'),
          body: {
            toNumber: phoneNumber,
            leadId: leadId,
          },
          auth: true,
        });

        const result = await callService.initiateCall({
          to: phoneNumber,
          from: undefined,
          lead_id: leadId,
          recording_enabled: true,
          metadata: {
            lead_name: lead?.name || "Unknown",
            lead_email: lead?.email || "",
            source: "lead_history_callback_forwarding",
          }
        });

        if (result && result.call_id) {
          toast({
            title: "Call Forwarding",
            description: `Your phone will ring to connect to the caller`,
          });
        }
        return;
      }

      // Use browser calling (modern method)
      if (!isDeviceReady) {
        toast({
          title: "Device Not Ready",
          description: isDeviceRegistering ? "Initializing calling device..." : "Calling device is not ready. Please refresh the page or enable call forwarding in Settings.",
          variant: "destructive",
        });
        return;
      }

      // Request microphone permission before starting call
      const { requestMicrophoneWithGuidance } = await import("@/app/_module/utils/microphone-permission");
      const permissionResult = await requestMicrophoneWithGuidance();

      if (!permissionResult.granted) {
        toast({
          title: "Microphone Access Required",
          description: permissionResult.error || "Please allow microphone access to make calls.",
          variant: "destructive",
        });
        return;
      }

      // Create session record
      await api.post({
        url: getApiUrl('/communication/sessions/manual-call'),
        body: {
          toNumber: phoneNumber,
          leadId: leadId,
        },
        auth: true,
      });

      // Use Twilio Voice SDK browser calling
      // Backend smart area code matching selects the best caller ID per lead
      const call = await connect({
        to: phoneNumber,
        leadId: leadId,
        metadata: {
          lead_name: lead?.name || "Unknown",
          lead_email: lead?.email || "",
          source: "lead_history_callback",
        }
      });

      if (call) {
        const formatPhone = (phone: string) => {
          const cleaned = phone.replace(/\D/g, '');
          if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
          }
          if (cleaned.length === 11 && cleaned.startsWith('1')) {
            return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
          }
          return phone;
        };
        toast({
          title: "Call Connected",
          description: `Calling back ${formatPhone(phoneNumber)}`,
        });
      }
    } catch (error) {
      toast({
        title: "Call Failed",
        description: "Failed to initiate callback. Please try again.",
        variant: "destructive",
      });
    }
  }, [leadId, lead, forwardCallsToPhone, isDeviceReady, isDeviceRegistering, connect, selectBestCallerId]);

  // Handle ?action=call or ?action=sms from PolicyGuard (one-time on load)
  const actionHandledRef = useRef(false);
  useEffect(() => {
    if (actionHandledRef.current) return;
    const action = searchParams?.get('action');
    if (!action || !lead) return;

    actionHandledRef.current = true;

    if (action === 'call' && lead.phone) {
      // Small delay to let Twilio device register
      setTimeout(() => handleCallBack(lead.phone!), 500);
    } else if (action === 'sms' && lead.phone) {
      setShowSmsChat(true);
    } else if (action === 'requote') {
      // Handled by passing openInsuraBot prop to LeadDetailsLayout
    }
  }, [searchParams, lead, handleCallBack]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const previousFormDataRef = useRef<string>('');
  const isSavingRef = useRef(false); // Lock to prevent concurrent saves
  const hasPopulatedFormRef = useRef(false); // Track if form has been populated initially
  const originalCustomFieldsRef = useRef<string>('{}'); // Track original customFields to avoid unnecessary updates - default to empty object string

  const form = useForm<UpdateLeadSchema>({
    resolver: zodResolver(updateLeadSchema),
    mode: "onChange",
    defaultValues: {
      ...LEAD_FORM_DEFAULTS,
      beneficiaries: [],
    },
  });

  /**
   * ✅ FIXED: Populate form with ALL data types (not just strings)
   * This was the main issue - only string values were being set
   *
   * IMPORTANT: Only populate the form ONCE on initial load to prevent
   * overwriting user edits when the query refetches after auto-save
   */
  useEffect(() => {
    // Only populate form once when lead data first arrives
    if (lead && form && !hasPopulatedFormRef.current) {
      hasPopulatedFormRef.current = true;

      const formKeys = Object.keys(
        form.getValues()
      ) as (keyof typeof LEAD_FORM_DEFAULTS)[];

      formKeys.forEach((fieldKey) => {
        // Skip beneficiaries - they're loaded separately
        if (fieldKey === 'beneficiaries') return;

        const value = lead?.[fieldKey];

        // ✅ FIXED: Handle ALL data types, not just strings
        if (value !== undefined && value !== null) {

          // Special handling for customFields - keep as object
          if (fieldKey === 'customFields') {
            // Ensure customFields is a plain object
            if (typeof value === 'object' && !Array.isArray(value)) {
              form.setValue(fieldKey, value);
              // Store original customFields for comparison during save
              originalCustomFieldsRef.current = stableStringify(value);
            } else {
              form.setValue(fieldKey, {});
              originalCustomFieldsRef.current = stableStringify({});
            }
            return;
          }

          // Convert value to appropriate type based on field
          if (typeof value === 'string') {
            // Normalize state values to 2-letter codes so the dropdown can match
            form.setValue(fieldKey, fieldKey === 'state' && value ? getStateCode(value) : value);
          } else if (typeof value === 'number') {
            // Handle numeric fields (age, height, weight, etc.)
            form.setValue(fieldKey, String(value));
          } else if (typeof value === 'boolean') {
            // Handle boolean fields
            form.setValue(fieldKey, value ? 'true' : 'false');
          } else if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
            // Handle date fields - use local timezone to avoid UTC date shift
            if (value instanceof Date) {
              const year = value.getFullYear();
              const month = String(value.getMonth() + 1).padStart(2, '0');
              const day = String(value.getDate()).padStart(2, '0');
              form.setValue(fieldKey, `${year}-${month}-${day}`);
            } else {
              form.setValue(fieldKey, value);
            }
          } else {
            // Handle other objects/arrays by converting to string
            form.setValue(fieldKey, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        } else {
          // Set empty string/object for undefined/null values
          if (fieldKey === 'customFields') {
            form.setValue(fieldKey, {});
            // Store original as empty object so comparisons work correctly
            originalCustomFieldsRef.current = stableStringify({});
          } else {
            form.setValue(fieldKey, "");
          }
        }
      });

      // Mark initial load complete and store initial form data using stable stringify
      setTimeout(() => {
        isInitialLoadRef.current = false;
        previousFormDataRef.current = stableStringify(form.getValues());
      }, 500);
    }
  }, [lead, form]);

  // Auto-save function (silent, no toast on success)
  const performAutoSave = useCallback(async (showToast = false) => {
    // Prevent concurrent saves and skip during initial load
    if (!leadId || isInitialLoadRef.current || isSavingRef.current) return;

    const formData = form.getValues();
    // `leadStat` is deliberately NOT auto-saved. The form is populated once on
    // load (see hasPopulatedFormRef) and never re-synced, while the status is
    // owned by the header dropdown, which writes it through its own mutation.
    // Including it here replayed the page-load status ~2s after every dropdown
    // change and silently reverted it:
    //
    //   16:06:11  New lead → Underwriting   (dropdown — correct)
    //   16:06:12  Underwriting → New lead   (this autosave, stale value)
    //
    // Reported 2026-08-09 as "set it to Underwriting and it goes back". The
    // leads TABLE was unaffected because it has no form and no autosave.
    const { beneficiaries, leadStat: _ignoredLeadStat, ...leadData } = formData;

    // Ensure customFields is always a plain object
    let customFieldsValue = leadData.customFields;
    if (!customFieldsValue || typeof customFieldsValue !== 'object' || Array.isArray(customFieldsValue)) {
      customFieldsValue = {};
    }

    // Filter out empty strings and undefined values for cleaner data
    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(leadData)) {
      if (key === 'customFields') {
        // Only include customFields if it has actually changed from the original
        // This prevents the server from logging "Custom Fields" as updated when nothing changed
        const currentCustomFieldsStr = stableStringify(customFieldsValue);
        if (currentCustomFieldsStr !== originalCustomFieldsRef.current) {
          cleanData.customFields = customFieldsValue;
        }
      } else if (value !== undefined && value !== null && value !== "") {
        cleanData[key] = value;
      }
    }

    // Clean phone number if present
    if (cleanData.phone && typeof cleanData.phone === 'string') {
      cleanData.phone = extractPhoneNumber(cleanData.phone);
    }

    // Set lock
    isSavingRef.current = true;

    try {
      setIsAutoSaving(true);

      // Save lead data
      await updateLeadMutation({ leadId, data: cleanData });

      // Handle beneficiaries if present and have changes
      if (beneficiaries && beneficiaries.length > 0) {
        const newBeneficiaries = beneficiaries.filter(b => !b.id);
        const existingBeneficiaries = beneficiaries.filter(b => b.id);

        // Create new beneficiaries
        if (newBeneficiaries.length > 0) {
          await createBeneficiariesMutation.mutateAsync({
            leadId,
            beneficiaries: newBeneficiaries.map(b => ({
              name: b.name,
              relationship: b.relationship,
              percentage: b.percentage,
              dateOfBirth: b.dob,
              ssn: b.ssn,
              contingentName: b.contingentName,
              contingentRelationship: b.contingentRelationship,
              contingentPercentage: b.contingentPercentage,
            })),
          });
        }

        // Update existing beneficiaries
        for (const beneficiary of existingBeneficiaries) {
          if (beneficiary.id) {
            await updateBeneficiaryMutation.mutateAsync({
              beneficiaryId: beneficiary.id,
              leadId,
              data: {
                name: beneficiary.name,
                relationship: beneficiary.relationship,
                percentage: beneficiary.percentage,
                dob: beneficiary.dob,
                ssn: beneficiary.ssn,
                contingentName: beneficiary.contingentName,
                contingentRelationship: beneficiary.contingentRelationship,
                contingentPercentage: beneficiary.contingentPercentage,
              },
            });
          }
        }
      }

      setLastSavedAt(new Date());

      // Update previousFormDataRef to current form state using stable stringify
      // This prevents false positives from object key ordering differences
      previousFormDataRef.current = stableStringify(form.getValues());

      // Update originalCustomFieldsRef so future saves only include customFields if they change again
      const currentCustomFields = form.getValues('customFields') || {};
      originalCustomFieldsRef.current = stableStringify(currentCustomFields);

      if (showToast) {
        toast({
          title: "✅ Changes Saved",
          description: "Your changes have been saved.",
        });
      }
    } catch (error: any) {
      // Silent fail for auto-save, user can retry with manual save
      // Enhanced error logging to capture actual error details
      console.error('Auto-save failed:', {
        message: error?.message || 'Unknown error',
        status: error?.status || error?.response?.status,
        data: error?.data || error?.response?.data,
        error: error,
      });
    } finally {
      setIsAutoSaving(false);
      isSavingRef.current = false; // Release lock
    }
  }, [leadId, form, updateLeadMutation, createBeneficiariesMutation, updateBeneficiaryMutation]);

  // Watch for form changes and trigger auto-save with debounce
  // Use a ref to avoid re-creating effect when performAutoSave changes
  const performAutoSaveRef = useRef(performAutoSave);
  performAutoSaveRef.current = performAutoSave;

  useEffect(() => {
    const subscription = form.watch((formData) => {
      // Skip during initial load or while saving
      if (isInitialLoadRef.current || isSavingRef.current) return;

      // Check if data actually changed using stable stringify
      const currentData = stableStringify(formData);
      if (currentData === previousFormDataRef.current) return;

      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for auto-save (2 second debounce)
      autoSaveTimeoutRef.current = setTimeout(() => {
        performAutoSaveRef.current(false);
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form]); // Remove performAutoSave from deps to prevent infinite loop

  // Cleanup on unmount - save any pending changes
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        // Perform final save on unmount if there are pending changes
        const currentData = stableStringify(form.getValues());
        if (currentData !== previousFormDataRef.current && !isInitialLoadRef.current) {
          performAutoSaveRef.current(false);
        }
      }
    };
  }, [form]); // Use ref instead of performAutoSave to avoid dependency issues

  // Show loading state
  if (isLoading || policiesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state only if there's an actual error
  if (error || !lead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading lead details</p>
          <p className="text-sm text-gray-500 mb-4">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            className="text-blue-600 hover:text-blue-800"
            onClick={goBack}
          >
            Back to Leads
          </button>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    const formData = form.getValues();

    // Extract beneficiaries from form data.
    // `leadStat` is excluded for the same reason as in performAutoSave: the
    // form holds the status from page load and never re-syncs, so submitting it
    // reverts whatever the header dropdown last set. Status changes go through
    // the dropdown's own mutation.
    const { beneficiaries, leadStat: _ignoredLeadStat, ...leadData } = formData;

    // Check if assignment changed
    const originalAssigned = lead?.assigned || '';
    const newAssigned = formData.assigned || '';
    const assignmentChanged = originalAssigned !== newAssigned;

    // Ensure customFields is always a plain object
    let customFieldsValue = leadData.customFields;
    if (!customFieldsValue || typeof customFieldsValue !== 'object' || Array.isArray(customFieldsValue)) {
      customFieldsValue = {};
    }

    // Filter out empty strings and undefined values for cleaner data
    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(leadData)) {
      if (key === 'customFields') {
        // Only include customFields if it has actually changed from the original
        // This prevents the server from logging "Custom Fields" as updated when nothing changed
        const currentCustomFieldsStr = stableStringify(customFieldsValue);
        if (currentCustomFieldsStr !== originalCustomFieldsRef.current) {
          cleanData.customFields = customFieldsValue;
        }
      } else if (value !== undefined && value !== null && value !== "") {
        cleanData[key] = value;
      }
    }

    // Clean phone number if present
    if (cleanData.phone && typeof cleanData.phone === 'string') {
      cleanData.phone = extractPhoneNumber(cleanData.phone);
    }

    if (!leadId) {
      toast({
        title: "Missing Lead ID",
        description: "Cannot update lead without a valid ID.",
      });
      return;
    }

    try {
      // 1. Save lead data
      const result = await updateLeadMutation({ leadId, data: cleanData });
      
      // 2. Dispatch events if assignment changed
      if (assignmentChanged) {
        if (newAssigned && newAssigned.trim()) {
          window.dispatchEvent(new CustomEvent('lead-assigned'));
        } else {
          window.dispatchEvent(new CustomEvent('lead-unassigned'));
        }
      }

      // 3. Handle beneficiaries if present
      if (beneficiaries && beneficiaries.length > 0 && leadId) {
        setIsSavingBeneficiaries(true);
        
        try {
          const newBeneficiaries = beneficiaries.filter(b => !b.id);
          const existingBeneficiaries = beneficiaries.filter(b => b.id);

          // Create new beneficiaries
          if (newBeneficiaries.length > 0) {
            await createBeneficiariesMutation.mutateAsync({
              leadId,
              beneficiaries: newBeneficiaries.map(b => ({
                name: b.name,
                relationship: b.relationship,
                percentage: b.percentage,
                dateOfBirth: b.dob,
                ssn: b.ssn,
                contingentName: b.contingentName,
                contingentRelationship: b.contingentRelationship,
                contingentPercentage: b.contingentPercentage,
              })),
            });
          }

          // Update existing beneficiaries
          for (const beneficiary of existingBeneficiaries) {
            if (beneficiary.id) {
              await updateBeneficiaryMutation.mutateAsync({
                beneficiaryId: beneficiary.id,
                leadId,
                data: {
                  name: beneficiary.name,
                  relationship: beneficiary.relationship,
                  percentage: beneficiary.percentage,
                  dob: beneficiary.dob,
                  ssn: beneficiary.ssn,
                  contingentName: beneficiary.contingentName,
                  contingentRelationship: beneficiary.contingentRelationship,
                  contingentPercentage: beneficiary.contingentPercentage,
                },
              });
            }
          }
        } catch (beneficiaryError) {
          toast({
            title: "⚠️ Partial Save",
            description: "Lead saved but some beneficiaries failed to save.",
          });
          return;
        } finally {
          setIsSavingBeneficiaries(false);
        }
      }

      toast({
        title: "✅ Lead Updated",
        description: `${result?.data?.lead?.name || "Lead updated successfully"}`,
      });

      // Refetch data to show updated values
      await refetch();
      await refetchPolicies();
    } catch (error: unknown) {
      let errorMessage = "An error occurred while updating the lead.";
      
      if (error && typeof error === 'object' && 'message' in error) {
        const fullMessage = error.message as string;
        
        if (fullMessage.includes('must be one of') || fullMessage.includes('must be a valid')) {
          const errors = fullMessage.split(/,(?=\s*[A-Z])/);
          const firstError = errors[0]?.trim();
          
          if (firstError) {
            if (firstError.includes('Marital status must be one of')) {
              errorMessage = "Marital Status: Please select an option";
            } else if (firstError.includes('Employment status must be one of')) {
              errorMessage = "Employment Status: Please select an option";
            } else if (firstError.includes('Gender must be one of')) {
              errorMessage = "Gender: Please select an option";
            } else if (firstError.includes('Email must be a valid')) {
              errorMessage = "Email: Invalid email format";
            } else if (firstError.includes('Phone must be a valid')) {
              errorMessage = "Phone: Invalid phone format";
            } else {
              errorMessage = firstError.length > 50 ? firstError.substring(0, 47) + "..." : firstError;
            }
          }
        } else {
          errorMessage = fullMessage;
        }
      }
      
      toast({
        title: "❌ Update Failed",
        description: errorMessage,
      });
    }
  };

  const handleCancel = async () => {
    // Refetch fresh data from server
    const freshData = await refetch();
    const freshLead = unwrapApiResponse(freshData.data);

    if (freshLead && form) {
      // Reset the populated flag so the useEffect can repopulate if needed
      // But we'll manually populate here anyway
      const formKeys = Object.keys(
        form.getValues()
      ) as (keyof typeof LEAD_FORM_DEFAULTS)[];

      formKeys.forEach((fieldKey) => {
        if (fieldKey === 'beneficiaries') return;

        const value = freshLead?.[fieldKey];
        if (value !== undefined && value !== null) {
          // Special handling for customFields - keep as object
          if (fieldKey === 'customFields') {
            if (typeof value === 'object' && !Array.isArray(value)) {
              form.setValue(fieldKey, value);
            } else {
              form.setValue(fieldKey, {});
            }
            return;
          }

          if (typeof value === 'string') {
            form.setValue(fieldKey, fieldKey === 'state' && value ? getStateCode(value) : value);
          } else if (typeof value === 'number') {
            form.setValue(fieldKey, String(value));
          } else if (typeof value === 'boolean') {
            form.setValue(fieldKey, value ? 'true' : 'false');
          } else if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
            form.setValue(fieldKey, value instanceof Date ? value.toISOString().split('T')[0] : value);
          } else {
            form.setValue(fieldKey, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        } else {
          if (fieldKey === 'customFields') {
            form.setValue(fieldKey, {});
          } else {
            form.setValue(fieldKey, "");
          }
        }
      });

      // Update the previousFormData ref to match the fresh data using stable stringify
      previousFormDataRef.current = stableStringify(form.getValues());

      // Also update originalCustomFieldsRef to match the fresh data
      const freshCustomFields = form.getValues('customFields') || {};
      originalCustomFieldsRef.current = stableStringify(freshCustomFields);

      toast({
        title: "Form Reset",
        description: "Form has been reset to the last saved values.",
      });
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      await updateLeadMutation({
        leadId,
        data: { leadStat: newStatus }
      });

      // Track lead status for session complete stats (if there's an active call - browser OR bridged)
      if (hasActiveCall) {
        trackManualCallLeadStatus(newStatus);
      }

      await refetch();
      await refetchPolicies();

      toast({
        title: "✅ Status Updated",
        description: `Lead status changed to ${newStatus}`,
      });
    } catch (error: unknown) {
      let errorMessage = "Failed to update lead status";
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = error.message as string;
      }

      toast({
        title: "❌ Update Failed",
        description: errorMessage,
      });
      throw error;
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <LeadDetailsHeader
          isEditing={isEditing}
          viewOnly={viewOnly}
          isUpdating={isUpdating || isSavingBeneficiaries}
          isAutoSaving={isAutoSaving}
          lastSavedAt={lastSavedAt}
          lead={lead as unknown as Lead}
          onBack={goBack}
          onCancel={() => {
            handleCancel();
            setIsEditing(false);
          }}
          onEdit={() => { if (!viewOnly) setIsEditing(true); }}
          onSave={handleSave}
          onStatusChange={handleStatusChange}
          hasPolicies={hasPolicies}
          policyCount={policyCount}
          policies={policies}
          onPolicyUpdate={() => refetchPolicies()}
          // Lead navigation props - always show arrows, disable if not available
          onPrevLead={handlePrevLead}
          onNextLead={handleNextLead}
          hasPrevLead={hasPrevLead ?? undefined}
          hasNextLead={hasNextLead ?? undefined}
          currentLeadIndex={hasLeadNavigation && navigationContext ? ((navigationContext.currentPage - 1) * navigationContext.limit) + currentLeadIndex : undefined}
          totalLeads={navigationContext?.totalLeads}
          isLoadingNavigation={isLoadingNavigation || isLoadingPage}
        />

        <Separator />

        {/* View-only banner — shown when this lead was shared with the user
            (e.g. they were @mentioned) but belongs to another agent. Explains
            why actions are disabled so the agent isn't confused. */}
        {viewOnly && (
          <div className="flex items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 text-red-900">
            <Eye className="h-5 w-5 mt-0.5 shrink-0 text-red-600" aria-hidden />
            <div className="text-sm">
              <span className="font-bold uppercase">View only</span> —{' '}
              {viewOnlyOwner ? (
                <>
                  this lead is assigned to <span className="font-semibold">{viewOnlyOwner}</span> and stays on
                  their pipeline.
                </>
              ) : (
                <>this lead was shared with you and stays on the owning agent&rsquo;s pipeline.</>
              )}{' '}
              You can see everything and <span className="font-semibold">add notes</span>, but editing, calling,
              texting, and scheduling are turned off. To work it, ask{' '}
              {viewOnlyOwner ? 'them' : 'the owner'} to assign it to you.
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <LeadActionButtons
          lead={lead as unknown as Lead}
          viewOnly={viewOnly}
          onEdit={() => {}}
          onCallInitiated={() => {}}
          onSmsOpen={() => { if (!viewOnly) setShowSmsChat(true); }}
          onStatusChange={(leadId, newStatus) => {
            // Track status change for session complete stats (if there's an active call)
            if (hasActiveCall) {
              trackManualCallLeadStatus(newStatus);
            }
          }}
        />

        {/* Fresh Lead Banner intentionally NOT rendered in lead details. The
            whole point of Fresh Lead / Clean Slate is that the ASSIGNED AGENT
            must never know activity was hidden — and the viewer-role signals
            here (global user_type='agency_owner', role fallback to 'OWNER')
            leaked the banner to agents, defeating the feature. Owners manage
            Fresh Lead + Restore Activity from the Lead Assignment surface. */}

        {/* Main Content */}
        <FormProvider {...form}>
          <LeadDetailsLayout
            // embedded (leadIdProp set): host URL's ?tab= (e.g. agency-dashboard's
            // tab=users) must NOT drive the lead tabs — always start on Details
            embedded={!!leadIdProp}
            hasPolicies={hasPolicies}
            isEditing={isEditing}
            leadId={leadId}
            policies={policies}
            policyCount={policyCount}
            initialNotes={leadWithPolicies?.comments || []}
            leadData={leadWithPolicies}
            onStatusChange={handleStatusChange}
            onCallBack={handleCallBack}
            onOpenSmsWidget={() => setShowSmsChat(true)}
            openInsuraBot={searchParams?.get('action') === 'requote'}
          />
        </FormProvider>
      </div>

      {/* SMS Chat Widget */}
      {showSmsChat && leadWithPolicies?.phone && leadId && (
        <SmsChatWidget
          leadName={leadWithPolicies?.firstName && leadWithPolicies?.lastName ? `${leadWithPolicies.firstName} ${leadWithPolicies.lastName}` : "Unknown"}
          phoneNumber={leadWithPolicies.phone}
          leadId={leadId}
          messages={smsMessages}
          onClose={() => setShowSmsChat(false)}
          onSendMessage={async (message) => {
            const tempId = Date.now().toString();
            setSmsMessages((prev) => [...prev, {
              id: tempId,
              text: message,
              direction: "outbound" as const,
              timestamp: new Date(),
              status: "sending" as const,
            }]);

            try {
              const { sendSms } = await import("@/app/(authenticated)/communication/_modules/services/sendSms");
              const result = await sendSms({
                to: leadWithPolicies?.phone || "",
                text: message,
              });

              // sendSms returns null on non-throwing failures (A2P block,
              // opt-out) — surface as failed since the bubble was optimistic.
              if (!result) {
                setSmsMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === tempId ? { ...msg, status: "failed" as const } : msg
                  )
                );
                return;
              }

              const sentMessageSid = (result as any)?.data?.messageSid as string | undefined;
              setSmsMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempId
                    ? { ...msg, status: "sent" as const, messageSid: sentMessageSid }
                    : msg
                )
              );
            } catch (error) {
              setSmsMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempId ? { ...msg, status: "failed" as const } : msg
                )
              );
            }
          }}
        />
      )}
    </>
  );
};

export default LeadFullDetails;
export { LeadFullDetails };